import React, { useId, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useWikiV2Client } from '../../../hooks/useWikiV2';
import WikiIcon, { WIKI_CATEGORY_ICON_CHOICES } from '../shared/WikiIcon';
import { WIKI_CATEGORY_PALETTE } from '../shared/wikiFormat';
import { invalidateWiki, useWikiCategories } from '../shared/useWikiQueries';
import useWikiRole from '../shared/useWikiRole';
import { wikiPaths } from '../shared/wikiRoutes';
import { wikiUi } from '../shared/wikiUi';
import WikiModalShell, {
    WikiCreateButton,
    WikiField,
    WikiModalError,
    WikiSegmented,
    WikiToggleCard,
} from './WikiModalShell';
import {
    WIKI_ANSWER_OPTIONS,
    WIKI_MANAGE_COPY,
    WIKI_MODAL_COPY,
    WIKI_MODAL_TOASTS,
} from './wikiModalCopy';
import WikiAccessFields from '../access/WikiAccessFields';
import { useAccessAncestor, useWikiMembers } from '../access/useWikiAccess';
import {
    WIKI_ACCESS_COPY,
    accessChoicesReady,
    choicesToRules,
    defaultAccessChoices,
    isDefaultAccess,
} from '../access/wikiAccessRules';
import { wikiModalErrorText, wikiPlanRefusal } from './wikiModalErrors';
import { primeCreatedCategory } from './wikiModalCache';

/**
 * Prototype opener state (`wkOpenNewCat` / `addSub`, C2 §1.1). With
 * `category` (Settings, #872) the form starts from that category's values.
 */
export function initialCategoryForm(isSub, category = null) {
    const form = {
        name: '',
        desc: '',
        icon: 'folder',
        color: WIKI_CATEGORY_PALETTE[0].color,
        // Who can view / edit (#871): role defaults for a category, the
        // parent's access for a subcategory. Wiki admins can change it.
        access: defaultAccessChoices(isSub),
        exact: 'Rewrite clearly',
        agent: true,
    };
    if (!category) return form;
    return {
        ...form,
        name: category.name || '',
        desc: category.description || '',
        icon: category.icon || form.icon,
        color: category.color || form.color,
        exact: category.answer_mode === 'quote_exact' ? 'Quote exactly' : 'Rewrite clearly',
        agent: category.assistant_enabled !== false,
    };
}

/**
 * The POST /wiki/categories body for a form (BE-core contract §3). Access
 * choices travel as `access_rules` (#871), saved by the server together with
 * the category; the old descriptive labels are no longer sent.
 */
export function categoryPayload(form, { isSub, parentId, accessRules = [] }) {
    const body = {
        name: form.name.trim(),
        description: form.desc,
        answer_mode: form.exact === 'Quote exactly' ? 'quote_exact' : 'rewrite',
        assistant_enabled: form.agent !== false,
    };
    if (accessRules.length) body.access_rules = accessRules;
    if (isSub) {
        body.parent_id = parentId;
    } else {
        const pair = WIKI_CATEGORY_PALETTE.find((p) => p.color === form.color) || WIKI_CATEGORY_PALETTE[0];
        body.icon = form.icon;
        body.color = pair.color;
        body.tint = pair.tint;
    }
    return body;
}

/**
 * The PATCH /wiki/categories/{id} body for Settings (#872): only the fields
 * that changed, in API names. Both sides go through `categoryPayload`, so the
 * form → API mapping lives in one place. Never sends the parent, the owner or
 * access (the Access panel owns access, #871). The colour is compared on the
 * raw value: a custom colour outside the palette isn't rewritten unless the
 * admin picks another swatch, and colour and tint always travel together.
 */
export function categoryChanges(initial, form, { isSub }) {
    // Whitespace-only edits to the description are not a change.
    const before = categoryPayload({ ...initial, desc: (initial.desc || '').trim() }, { isSub, parentId: null });
    const after = categoryPayload({ ...form, desc: (form.desc || '').trim() }, { isSub, parentId: null });
    const out = {};
    Object.keys(after).forEach((key) => {
        if (key === 'parent_id' || key === 'access_rules' || key === 'color' || key === 'tint') return;
        if (after[key] !== before[key]) out[key] = after[key];
    });
    if (!isSub && form.color !== initial.color) {
        out.color = after.color;
        out.tint = after.tint;
    }
    return out;
}

/**
 * New category / New subcategory in {Parent} (C2 §1.3–1.4).
 *
 * Category: NAME* · DESCRIPTION · ICON AND COLOR (10 tiles, 7 swatches; the
 * selected tile takes the colour) · WHO CAN VIEW · WHO CAN EDIT · HOW ASK THE
 * WIKI USES THESE PAGES (+ live hint) · Category assistant. Subcategory: the
 * parent note after NAME, no icon/colour, access starts as "Same as {parent}".
 *
 * Create → POST /wiki/categories → cache primed + invalidated → land on the
 * new (sub)category page with its row expanded in the nav → prototype toast.
 * There is no owner field (prototype): the server makes the creator the
 * owner. "Who can view / edit" (#871) is shown to Wiki admins only, and every
 * option is a real access rule: "Specific roles" / "Specific people" open a
 * picker, and the rules go in the same POST, checked (wiki:admin + MFA) before
 * anything is written. Editors can't save rules, so they see a note instead.
 * The prototype only stored labels that changed nothing. The subcategory's
 * answer mode and assistant ARE saved (the prototype dropped them — C2 §1.10 #4).
 *
 * Failures: the server's message inline; a plan refusal (403
 * `wiki_limit_reached`) closes the modal and toasts its message instead, so
 * the app's upgrade modal (PlanErrorInterceptor) is not hidden behind it.
 */
export default function WikiCategoryModal({ kind, opts = {}, onClose }) {
    // Settings (#872): the same form, seeded from `opts.category` and saved
    // with PATCH (changed fields only). Access stays in the Access panel (#871).
    const isEdit = kind === 'categorySettings';
    const editing = isEdit ? (opts.category || null) : null;
    const editingId = editing ? (editing.category_id || editing.id || null) : null;
    const isSub = isEdit ? Boolean(editing && editing.parent_id) : kind === 'subcategory';
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const client = useWikiV2Client();
    const categories = useWikiCategories();
    const [initialForm] = useState(() => initialCategoryForm(isSub, editing));
    const [form, setForm] = useState(initialForm);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const nameId = useId();
    const descId = useId();
    const role = useWikiRole();
    const canSetAccess = role.canAdmin && !isEdit;

    const parentId = isSub ? ((editing && editing.parent_id) || opts.parentId || opts.categoryId || null) : null;
    const parentHit = parentId ? categories.find(parentId) : null;
    const parentName = (parentHit && parentHit.category && parentHit.category.name)
        || (opts.category && opts.category.name) || opts.parentName || '';

    const set = (key) => (value) => setForm((prev) => ({ ...prev, [key]: value }));

    const customAccess = canSetAccess && !isDefaultAccess(form.access, isSub);
    const ancestor = useAccessAncestor(parentId, { enabled: customAccess && isSub });
    const membersQuery = useWikiMembers({ enabled: customAccess });
    const { rules: accessRules, notCarried } = useMemo(() => (customAccess
        ? choicesToRules(form.access, {
            nested: isSub,
            ancestorSummary: ancestor.summary,
            ancestorRows: ancestor.rows,
            members: membersQuery.members,
            roleFloors: membersQuery.roleFloors,
        })
        : { rules: [], notCarried: [] }), [
        customAccess, form.access, isSub, ancestor.summary, ancestor.rows, membersQuery.members, membersQuery.roleFloors,
    ]);
    const accessReady = !customAccess || (
        accessChoicesReady(form.access, {
            nested: isSub, ancestorSummary: ancestor.summary, roleFloors: membersQuery.roleFloors,
        })
        && ancestor.ready
    );
    const canCreate = Boolean(form.name.trim()) && (!isSub || Boolean(parentId)) && accessReady;
    // Why Create can't proceed when the people or parent rules didn't load
    // (e.g. an MFA refusal), instead of a silently disabled button.
    const accessLoadError = customAccess && (membersQuery.error || ancestor.error);
    const changes = isEdit ? categoryChanges(initialForm, form, { isSub }) : null;
    const canSave = isEdit && Boolean(editingId) && Boolean(form.name.trim()) && Object.keys(changes).length > 0;

    const create = async () => {
        if (!canCreate || busy) return;
        setBusy(true);
        setError('');
        try {
            const created = await client.post('/wiki/categories', categoryPayload(form, { isSub, parentId, accessRules }));
            const id = created && created.category_id;
            if (!id) throw new Error('The new category has no id.');
            primeCreatedCategory(queryClient, created);
            wikiUi.setNavOpen(isSub ? parentId : id, true);
            onClose();
            navigate(wikiPaths.category(id));
            const name = (created.parent && created.parent.name) || parentName;
            wikiUi.toast(isSub ? WIKI_MODAL_TOASTS.subcategoryCreated(name) : WIKI_MODAL_TOASTS.categoryCreated());
        } catch (err) {
            // Plan limit (403 wiki_limit_reached — e.g. "The Starter plan allows
            // 10 top-level categories…"): close so the app's upgrade modal is
            // on top, and say why in the toast.
            const refusal = wikiPlanRefusal(err);
            if (refusal) {
                onClose();
                wikiUi.toast(refusal.message);
                return;
            }
            setBusy(false);
            setError(wikiModalErrorText(err));
        }
    };

    const save = async () => {
        if (!canSave || busy) return;
        setBusy(true);
        setError('');
        try {
            await client.patch(`/wiki/categories/${encodeURIComponent(editingId)}`, changes);
            onClose();
            wikiUi.toast(WIKI_MODAL_TOASTS.categorySaved());
            // Name, icon and description show in the nav, header, Home and crumbs.
            await invalidateWiki(queryClient);
        } catch (err) {
            setBusy(false);
            setError(wikiModalErrorText(err));
        }
    };
    const submit = isEdit ? save : create;

    const onNameKeyDown = (event) => {
        if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
            event.preventDefault();
            submit();
        }
    };

    const createTitle = isSub ? WIKI_MODAL_COPY.titleSubcategory(parentName) : WIKI_MODAL_COPY.titleCategory;
    const title = isEdit ? WIKI_MANAGE_COPY.titleSettings(editing && editing.name) : createTitle;
    const createTestId = isSub ? 'wiki-subcategory-modal' : 'wiki-category-modal';

    return (
        <WikiModalShell
            title={title}
            onClose={onClose}
            testId={isEdit ? 'wiki-category-settings-modal' : createTestId}
            footer={(
                <WikiCreateButton
                    enabled={isEdit ? canSave : canCreate}
                    busy={busy}
                    onClick={submit}
                    icon={isEdit ? 'check' : 'plus'}
                    iconSize={15}
                    action={isEdit ? 'wiki.update_category' : (isSub ? 'wiki.create_subcategory' : 'wiki.create_category')}
                    testId={isEdit ? 'wiki-modal-save' : 'wiki-modal-create'}
                >
                    {isEdit ? WIKI_MANAGE_COPY.save : WIKI_MODAL_COPY.create}
                </WikiCreateButton>
            )}
        >
            <WikiField label={WIKI_MODAL_COPY.nameLabel} required htmlFor={nameId}>
                <input
                    id={nameId}
                    className="wk-modal-input"
                    value={form.name}
                    onChange={(event) => set('name')(event.target.value)}
                    onKeyDown={onNameKeyDown}
                    placeholder={WIKI_MODAL_COPY.namePlaceholder}
                    maxLength={160}
                    required
                    aria-required="true"
                    data-wk-autofocus
                    data-field={isSub ? 'wiki.subcategory_name' : 'wiki.category_name'}
                    data-testid="wiki-modal-name"
                />
            </WikiField>

            {isSub && !isEdit && (
                <div className="wk-modal-note" data-testid="wiki-modal-parent-note">
                    <WikiIcon name="folder" size={14} />
                    <span>
                        {canSetAccess ? WIKI_MODAL_COPY.parentNote(parentName) : WIKI_MODAL_COPY.parentNoteFixed(parentName)}
                    </span>
                </div>
            )}

            <WikiField label={WIKI_MODAL_COPY.descLabel} htmlFor={descId}>
                <textarea
                    id={descId}
                    className="wk-modal-input wk-modal-textarea"
                    value={form.desc}
                    onChange={(event) => set('desc')(event.target.value)}
                    placeholder={WIKI_MODAL_COPY.descPlaceholder}
                    maxLength={2000}
                    data-field="wiki.category_description"
                    data-testid="wiki-modal-desc"
                />
            </WikiField>

            {!isSub && (
                <WikiField label={WIKI_MODAL_COPY.iconColorLabel} variant="group" testId="wiki-modal-icon-color">
                    <div className="wk-icon-tiles" role="radiogroup" aria-label="Icon">
                        {WIKI_CATEGORY_ICON_CHOICES.map((icon) => {
                            const on = icon === form.icon;
                            return (
                                <button
                                    key={icon}
                                    type="button"
                                    role="radio"
                                    aria-checked={on}
                                    aria-label={icon.replace(/-/g, ' ')}
                                    title={icon.replace(/-/g, ' ')}
                                    className={`wk-icon-tile${on ? ' is-on' : ''}`}
                                    style={on ? { background: form.color } : undefined}
                                    onClick={() => set('icon')(icon)}
                                    data-icon={icon}
                                >
                                    <WikiIcon name={icon} size={16} />
                                </button>
                            );
                        })}
                    </div>
                    <div className="wk-swatches" role="radiogroup" aria-label="Color">
                        {WIKI_CATEGORY_PALETTE.map((pair) => {
                            const on = pair.color === form.color;
                            return (
                                <button
                                    key={pair.color}
                                    type="button"
                                    role="radio"
                                    aria-checked={on}
                                    aria-label={pair.color}
                                    title={pair.color}
                                    className="wk-swatch"
                                    style={{
                                        background: pair.color,
                                        boxShadow: on ? `0 0 0 2px var(--surface), 0 0 0 4px ${pair.color}` : 'none',
                                    }}
                                    onClick={() => set('color')(pair.color)}
                                    data-color={pair.color}
                                />
                            );
                        })}
                    </div>
                </WikiField>
            )}

            {isEdit ? null : canSetAccess ? (
                <WikiAccessFields
                    choices={form.access}
                    onChange={set('access')}
                    nested={isSub}
                    parentName={parentName}
                    members={membersQuery.members}
                    membersLoading={membersQuery.isLoading}
                    notCarried={notCarried}
                />
            ) : !isSub && (
                <div className="wk-modal-note" data-testid="wiki-modal-access-default">
                    <WikiIcon name="lock" size={14} />
                    <span>{WIKI_ACCESS_COPY.defaultNoteTop}</span>
                </div>
            )}

            <WikiField label={WIKI_MODAL_COPY.answerLabel} variant="group">
                <WikiSegmented
                    label={WIKI_MODAL_COPY.answerLabel}
                    options={WIKI_ANSWER_OPTIONS}
                    value={form.exact}
                    onChange={set('exact')}
                    testId="wiki-modal-answer"
                />
                <div className="wk-modal-hint wk-t-meta" data-testid="wiki-modal-answer-hint">
                    {WIKI_MODAL_COPY.answerHint[form.exact]}
                </div>
            </WikiField>

            <WikiToggleCard
                title={WIKI_MODAL_COPY.assistantTitle}
                description={isEdit ? WIKI_MANAGE_COPY.assistantDescSettings : WIKI_MODAL_COPY.assistantDesc}
                checked={form.agent}
                onChange={set('agent')}
                testId="wiki-modal-assistant"
            />

            <WikiModalError message={error || (accessLoadError ? wikiModalErrorText(accessLoadError) : '')} />
        </WikiModalShell>
    );
}
